import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";
import { FaqStatusPill } from "./FaqStatusPill";
import { formatLongDate, relativeFromNow } from "../date-utils";

export interface FaqRowData {
  id: string;
  question: string;
  answer: string;
  sortOrder: number | null;
  showOnFaqPage: boolean;
  updatedAt: string;
}

/**
 * One row in the FAQ list. Three lines: status pill + sort badge on
 * the metadata line, question as the display-serif anchor, stripped
 * answer preview as the subtitle. Same hover/focus treatment as the
 * blog and landing rows.
 */
export function FaqRow({ faq }: { faq: FaqRowData }) {
  const navigate = useNavigate();
  const preview = stripHtml(faq.answer);
  const longDate = formatLongDate(faq.updatedAt);
  const relative = relativeFromNow(faq.updatedAt) ?? "just now";

  return (
    <button
      type="button"
      onClick={() => navigate(`/website/faqs/${faq.id}`)}
      className="group relative flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors duration-150 hover:bg-paper-2 active:bg-canvas/60 focus-visible:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tide/40"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-2 left-0 w-[3px] origin-top -translate-x-1 scale-y-50 rounded-r bg-tide opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:scale-y-100 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:scale-y-100 group-focus-visible:opacity-100"
      />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <FaqStatusPill showOnFaqPage={faq.showOnFaqPage} />
          {faq.sortOrder != null && (
            <>
              <span className="text-[11px] text-ink-5">·</span>
              <span className="num text-[11px] text-ink-4">
                #{faq.sortOrder}
              </span>
            </>
          )}
        </div>
        <div className="font-display truncate text-[17px] font-medium text-ink transition-colors duration-150 group-hover:text-tide group-focus-visible:text-tide">
          {faq.question || "Untitled question"}
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-ink-4">
          {preview || "No answer yet."}
        </div>
      </div>
      <div className="flex-shrink-0 text-right text-[11px] text-ink-4">
        <div className="num">{longDate}</div>
        <div className="mt-0.5">{relative}</div>
      </div>
      <ChevronRight
        size={16}
        strokeWidth={1.8}
        aria-hidden
        className="flex-shrink-0 -translate-x-1 text-ink-5 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:text-tide group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
      />
    </button>
  );
}

/**
 * Strip tags + collapse whitespace for a clean single-line preview.
 * Decoded entities aren't a concern here — most answers use safe
 * HTML emitted by the WYSIWYG, and the truncate ellipsis hides any
 * trailing cruft.
 */
function stripHtml(html: string | null): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

